import { referenceLineParams, referenceLineSet, referenceType } from '@ps/types/workbench/stage.type'
import Konva from 'konva'
import StageData from '@ps/store/workbench/stage/stage-data'
import generateId from '@com/utils/generate-id'
import {
  referenceLineGroup,
  referenceLineOptions, referenceLinePanel, referenceLineCloseBtnGroup, referenceLineHoverArea
} from '@ps/store/workbench/stage/config/ruler-config'
import { Vector2d } from 'konva/lib/types'
import store from '@ps/store'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import { conversionPositionToStage, conversionStageToPosition } from '@ps/store/workbench/stage/utils/coord-transform'
import gameIframe from '@ps/bridge/iframe/iframe'
import { LAYOUT_RESOLUTION } from '@ps/bridge/qc/scripts/layout'

/**
 * 创建辅助线组里面的辅助线逻辑
 * @param type 辅助线类型
 */
export const createLine = (type: referenceType): Konva.Line => {
  const Stage = StageData.stage!
  let points: number[] = []
  if (type === 'horizontal') {
    // 需要设置 0.5 才可以描绘 1px 的线，参考： https://stackoverflow.com/questions/9311428/draw-single-pixel-line-in-html5-canvas
    points = [0, 0.5, Stage.width(), 0.5]
  } else if (type === 'vertical') {
    points = [0.5, 0, 0.5, Stage.height()]
  }
  return new Konva.Line({
    points: points,
    ...referenceLineOptions,
    referenceLineType: type
  })
}

/**
 * 创建辅助线显示面板
 * @param type 辅助线类型
 * @param text 辅助线显示像素值
 */
export const createPanel = ({ type, text }: {
  type: referenceType, text: string
}): Konva.Text => {
  return new Konva.Text({
    x: type === 'horizontal'
      ? referenceLinePanel.displayOffset.group
      : referenceLinePanel.displayOffset.line,
    y: type === 'vertical'
      ? referenceLinePanel.displayOffset.group
      : referenceLinePanel.displayOffset.line,
    fontSize: referenceLinePanel.fontSize,
    fill: referenceLinePanel.fill,
    name: referenceLinePanel.name,
    text: text
  })
}

export const createHoverArea = (type: referenceType): Konva.Rect => {
  return new Konva.Rect({
    x: type === 'horizontal'
      ? referenceLineHoverArea.displayOffset.group
      : referenceLineHoverArea.displayOffset.line,
    y: type === 'vertical'
      ? referenceLineHoverArea.displayOffset.group
      : referenceLineHoverArea.displayOffset.line,
    name: referenceLineHoverArea.name,
    width: type === 'vertical'
      ? referenceLineHoverArea.width
      : referenceLineHoverArea.height,
    height: type === 'horizontal'
      ? referenceLineHoverArea.width
      : referenceLineHoverArea.height
  })
}

/**
 * 创建辅助线删除按钮
 * @param type 辅助线类型
 */
export const createCloseBtn = (type: referenceType): Konva.Group => {
  const closeGroup = new Konva.Group({
    x: type === 'horizontal'
      ? referenceLineCloseBtnGroup.displayOffset.group
      : referenceLineCloseBtnGroup.displayOffset.line,
    y: type === 'vertical'
      ? referenceLineCloseBtnGroup.displayOffset.group
      : referenceLineCloseBtnGroup.displayOffset.line,
    name: referenceLineCloseBtnGroup.name
  })
  const closeLine1 = new Konva.Line({
    points: referenceLineCloseBtnGroup.points[0],
    strokeWidth: referenceLineCloseBtnGroup.strokeWidth,
    stroke: referenceLineCloseBtnGroup.stroke
  })
  const closeLine2 = new Konva.Line({
    points: referenceLineCloseBtnGroup.points[1],
    strokeWidth: referenceLineCloseBtnGroup.strokeWidth,
    stroke: referenceLineCloseBtnGroup.stroke
  })
  const closeRect = new Konva.Rect({
    width: 10,
    height: 10
  })
  closeGroup.add(closeLine1, closeLine2, closeRect)
  return closeGroup
}

/**
 * 创建辅助线
 * @param id
 * @param type
 * @param pointerPosition
 */
export function createReferenceLine({
  id, type, pointerPosition, isTemp
}: referenceLineParams): Konva.Group | void {
  const uniqueId = id || generateId()
  const gamePosition = {
    x: Math.round(conversionStageToPosition(pointerPosition).x),
    y: Math.round(conversionStageToPosition(pointerPosition).y)
  }
  // 如果已经有这个位置的辅助线，不允许重复添加
  if (hadReferenceLine(type, gamePosition, uniqueId)) {
    console.log('不允许重复添加')
    return
  }
  const reLineGroup = new Konva.Group({
    x: type === 'horizontal' ? 0 : pointerPosition.x,
    y: type === 'vertical' ? 0 : pointerPosition.y,
    id: uniqueId,
    ...referenceLineGroup,
    referenceLineType: type,
    dragBoundFunc: (pos: Vector2d): Vector2d => {
      if (type === 'horizontal') {
        return {
          x: 0,
          y: pos.y
        }
      } else if (type === 'vertical') {
        return {
          x: pos.x,
          y: 0
        }
      }
      return pos
    }
  })
  const showText = String(
    type === 'horizontal'
      ? gamePosition.y
      : gamePosition.x
  )
  const referenceLinePanel = createPanel({
    type,
    text: showText
  })
  const referenceLine = createLine(type)

  const referenceLineCloseBtnGroup = createCloseBtn(type)

  const referenceLineHoverAreaGroup = createHoverArea(type)

  reLineGroup.add(referenceLinePanel, referenceLine)
  // 临时数据没有删除按钮和相关事件
  if (isTemp) {
    StageData.layer?.add(reLineGroup)
  } else {
    reLineGroup.add(referenceLineHoverAreaGroup, referenceLineCloseBtnGroup)
    addReferenceLineEvent(reLineGroup)
    if (!StageData.referenceLineSets.find(item => item.id === uniqueId)) {
      StageData.referenceLineSets.push({
        id: uniqueId,
        type,
        x: gamePosition.x,
        y: gamePosition.y
      })
    }
    addDataToSceneNode()
    StageData.rulerLayer?.add(reLineGroup)
  }
  return reLineGroup
}

export function reDrawReferenceLine(params?: referenceLineSet[]): void {
  const Stage = StageData.stage!
  removeAllReferenceLine()
  // 如果 redraw 中含有传参，则赋值到 referenceLineSets 中去
  StageData.referenceLineSets = params || []
  StageData.referenceLineSets.forEach(item => {
    const position = conversionPositionToStage({
      x: item.x as number,
      y: item.y as number
    })
    const targetLineGroup = Stage.findOne(`#${item.id}`) as Konva.Group
    if (targetLineGroup) {
      const targetLine = targetLineGroup.findOne(`.${referenceLineOptions.name}`) as Konva.Line
      if (item.type === 'horizontal') {
        targetLine.points([0, 0, Stage.width(), 0])
        targetLineGroup.y(position.y)
      } else if (item.type === 'vertical') {
        targetLine.points([0, 0, 0, Stage.height()])
        targetLineGroup.x(position.x)
      }
    } else {
      createReferenceLine({
        id: item.id,
        type: item.type!,
        pointerPosition: position
      })
    }
  })
}

/**
 * 删除组所有事件
 * @param group
 */
export function removeAllEvent(group: Konva.Group): void {
  if (group) {
    group.off('.reLine')
  }
  group.findOne(`.${referenceLinePanel.name}`) && group.findOne(`.${referenceLinePanel.name}`).off('.reLine')
  group.findOne(`.${referenceLineCloseBtnGroup.name}`) && group.findOne(`.${referenceLineCloseBtnGroup.name}`).off('.reLine')
}

export function removeAllReferenceLine(): void {
  const Stage = StageData.stage!
  Stage.find(`.${referenceLineGroup.name}`).forEach(item => {
    const groupItem = item as Konva.Group
    removeAllEvent(groupItem)
    groupItem.destroy()
  })
  StageData.referenceLineSets = []
}

export function addDataToSceneNode() {
  const sceneNode = gameIframe.gamePlay
  const adapterResolution: LAYOUT_RESOLUTION = store.state.workspace.game.device.adapterResolution
  const ped = editorScript.getScript(sceneNode) as EditorDataScript
  if (!ped.$data.referenceLine) {
    ped.$data.referenceLine = {}
  }
  ped.$data.referenceLine[adapterResolution] = StageData.referenceLineSets
}

/**
 * 添加辅助线组事件
 * @param group
 */
export function addReferenceLineEvent(group: Konva.Group): void {
  const Stage = StageData.stage!
  const type = group.attrs.referenceLineType
  let hoverStatus = false
  const setCursorToResize = () => {
    if (type === 'horizontal') {
      Stage.container().style.cursor = 'ns-resize'
    } else {
      Stage.container().style.cursor = 'ew-resize'
    }
  }
  const setCursorToDefault = () => {
    Stage.container().style.cursor = 'default'
  }
  group.findOne(`.${referenceLineCloseBtnGroup.name}`)
    .on('click.reLine', () => {
      removeAllEvent(group)
      const targetSet = StageData.referenceLineSets.find(item => item.id === group.id())
      if (targetSet) {
        StageData.referenceLineSets.splice(StageData.referenceLineSets.indexOf(targetSet), 1)
      }
      addDataToSceneNode()
      group.destroy()
    })
  group.findOne(`.${referenceLineHoverArea.name}`)
    .on('mouseenter.reLine', () => {
      setCursorToResize()
    })
    .on('mouseleave.reLine', () => {
      if (hoverStatus) return
      setCursorToDefault()
    })
  group
    .on('dragstart.reLine', () => {
      hoverStatus = true
      setCursorToResize()
    })
    .on('dragmove.reLine', () => {
      const position = getAdjacentPosition({
        pointerPosition: group.position()
      })
      const gamePosition = conversionStageToPosition(position)
      if (type === 'horizontal') {
        group.y(position.y)
      } else if (type === 'vertical') {
        group.x(position.x)
      }
      showReferenceLinePanel({
        lineGroup: group,
        gamePosition,
        type
      })
      const targetSet = StageData.referenceLineSets.find(item => item.id === group.id())
      if (targetSet) {
        targetSet.x = gamePosition.x
        targetSet.y = gamePosition.y
      }
    })
    .on('dragend.reLine', () => {
      hoverStatus = false
      addDataToSceneNode()
      setCursorToDefault()
    })
}

/**
 * 获取相邻的数值， 默认步骤为十进制
 */
export function getAdjacentPosition({
  step, pointerPosition
}: {
  step?: number;
  pointerPosition: Vector2d
}): Vector2d {
  const numberStep = step || 10
  const gamePosition = conversionStageToPosition(pointerPosition)
  return conversionPositionToStage({
    x: Math.round(gamePosition.x / numberStep) * numberStep,
    y: Math.round(gamePosition.y / numberStep) * numberStep
  })
}

/**
 * 通过画布坐标来移动辅助线
 * @param lineGroup
 * @param pointerPosition
 * @param type
 */
export function moveReferenceLineByPosition({ lineGroup, pointerPosition, type }: {
  lineGroup: Konva.Group
  pointerPosition: Vector2d,
  type: referenceType
}) {
  // 四舍五入获取值
  const position = getAdjacentPosition({
    pointerPosition
  })
  if (type === 'horizontal') {
    lineGroup.y(position.y)
  } else if (type === 'vertical') {
    lineGroup.x(position.x)
  }
  showReferenceLinePanel({
    lineGroup,
    gamePosition: conversionStageToPosition(position),
    type
  })
}

/**
 * 判断是否已存在一样的辅助线
 * @param type
 * @param gamePosition
 */
export function hadReferenceLine(type: referenceType, gamePosition: Vector2d, uniqueId: string): boolean {
  return StageData.referenceLineSets.some(item => {
    if (item.id === uniqueId) return false
    if (item.type === 'horizontal') {
      return item.y === gamePosition.y
    } else if (item.type === 'vertical') {
      return item.x === gamePosition.x
    }
  })
}

export function showReferenceLinePanel({ lineGroup, type, gamePosition }: {
  lineGroup: Konva.Group,
  type: referenceType,
  gamePosition: Vector2d
}) {
  const textPanel = lineGroup.findOne(`.${referenceLinePanel.name}`) as Konva.Text
  if (textPanel) {
    textPanel.text(
      type === 'horizontal'
        ? `${Math.round(gamePosition.y)}`
        : `${Math.round(gamePosition.x)}`
    )
  }
}
